/**
 * Configuração de Ambiente para Produção
 * Gerenciamento centralizado de variáveis de ambiente
 */

// ==================== TIPOS ====================
export const Environment = {
  DEVELOPMENT: 'development',
  STAGING: 'staging',
  PRODUCTION: 'production',
  TEST: 'test',
} as const;

export type Environment = typeof Environment[keyof typeof Environment];

export interface EnvironmentConfig {
  env: Environment;
  isDevelopment: boolean;
  isStaging: boolean;
  isProduction: boolean;
  isTest: boolean;
  apiUrl: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  supabaseServiceRoleKey?: string;
  duffelApiUrl: string;
  duffelApiToken: string;
  duffelApiTokenTest?: string;
  duffelEnvironment: 'live' | 'test';
  appName: string;
  appUrl: string;
  appVersion: string;
  appDescription: string;
  appKeywords: string;
  enableLogging: boolean;
  enableAnalytics: boolean;
  enableDebugTools: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassword: string;
  smtpFromEmail: string;
  smtpFromName: string;
  adminEmails: string[];
  exchangeRateUsdAoa: number;
  exchangeRateEurAoa: number;
  defaultCurrency: string;
  sessionTimeout: number;
  maxLoginAttempts: number;
}

// ==================== CONSTANTES ====================
const REQUIRED_VARIABLES = {
  production: [
    'VITE_PUBLIC_SUPABASE_URL',
    'VITE_PUBLIC_SUPABASE_ANON_KEY',
    'VITE_APP_NAME',
    'VITE_APP_URL',
    'VITE_SMTP_HOST',
    'VITE_SMTP_PORT',
    'VITE_SMTP_USER',
    'VITE_SMTP_PASS',
    'VITE_FROM_EMAIL',
    'VITE_FROM_NAME',
  ],
  development: [
    'VITE_PUBLIC_SUPABASE_URL',
    'VITE_PUBLIC_SUPABASE_ANON_KEY',
  ],
};

const DEFAULT_VALUES = {
  appName: 'DG-Travel África Viagens e Turismo',
  appVersion: '2.0.0',
  appDescription: 'Agência de viagens credenciada IATA em Angola',
  appKeywords: 'viagens, voos, turismo, angola, luanda, passagens aéreas, vistos',
  duffelApiUrl: 'https://api.duffel.com',
  duffelEnvironment: 'live',
  exchangeRateUsdAoa: 919,
  exchangeRateEurAoa: 1075,
  defaultCurrency: 'AOA',
  sessionTimeout: 120,
  maxLoginAttempts: 5,
  smtpPort: 465,
};

// ==================== OBTER AMBIENTE ATUAL ====================
export const getCurrentEnvironment = (): Environment => {
  const hostname = window.location.hostname;
  const url = window.location.href;

  // Verificar por parâmetros de teste
  if (url.includes('test=true') || url.includes('mode=test')) {
    return Environment.TEST;
  }

  // Ambientes locais
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('.local')) {
    return Environment.DEVELOPMENT;
  }

  // Ambientes de staging/teste
  if (
    hostname.includes('staging') || 
    hostname.includes('test') || 
    hostname.includes('dev') ||
    hostname.includes('qa')
  ) {
    return Environment.STAGING;
  }

  // Produção
  return Environment.PRODUCTION;
};

// ==================== VALIDAR VARIÁVEIS DE AMBIENTE ====================
export const validateEnvironmentVariables = (): {
  valid: boolean;
  missing: string[];
  warnings: string[];
} => {
  const env = getCurrentEnvironment();
  const required = env === Environment.PRODUCTION 
    ? REQUIRED_VARIABLES.production 
    : REQUIRED_VARIABLES.development;

  const missing: string[] = [];
  const warnings: string[] = [];

  required.forEach((key) => {
    const value = import.meta.env[key];
    if (!value || value.trim().length === 0) {
      missing.push(key);
    } else if (value.includes('your_') || value.includes('xxx')) {
      warnings.push(`${key} contém valor placeholder`);
    }
  });

  // Validações específicas
  if (import.meta.env.VITE_SMTP_PORT) {
    const port = parseInt(import.meta.env.VITE_SMTP_PORT);
    if (isNaN(port) || port <= 0 || port > 65535) {
      warnings.push('VITE_SMTP_PORT deve ser uma porta válida (1-65535)');
    }
  }

  if (import.meta.env.VITE_EXCHANGE_RATE_USD_AOA) {
    const rate = parseFloat(import.meta.env.VITE_EXCHANGE_RATE_USD_AOA);
    if (isNaN(rate) || rate <= 0) {
      warnings.push('VITE_EXCHANGE_RATE_USD_AOA deve ser um número positivo');
    }
  }

  return {
    valid: missing.length === 0,
    missing,
    warnings,
  };
};

// ==================== OBTER CONFIGURAÇÃO ====================
export const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = getCurrentEnvironment();
  const isProduction = env === Environment.PRODUCTION;

  // Validar variáveis de ambiente
  const validation = validateEnvironmentVariables();
  if (!validation.valid) {
    console.error('❌ Variáveis de ambiente ausentes:', validation.missing);
    console.warn('⚠️ Aplicação iniciada com variáveis ausentes. Algumas funcionalidades podem não estar disponíveis.');
    validation.warnings.forEach(w => console.warn(w));
  }

  if (validation.warnings.length > 0) {
    validation.warnings.forEach(w => console.warn('⚠️', w));
  }

  // Determinar ambiente da Duffel
  const duffelEnvironment = (import.meta.env.VITE_DUFFEL_ENVIRONMENT || 
    (isProduction ? 'live' : 'test')) as 'live' | 'test';

  const duffelApiToken = duffelEnvironment === 'live' 
    ? import.meta.env.VITE_DUFFEL_API_TOKEN
    : import.meta.env.VITE_DUFFEL_API_TOKEN_TEST || import.meta.env.VITE_DUFFEL_API_TOKEN;

  // Emails administrativos
  const adminEmails = [
    import.meta.env.VITE_ADMIN_EMAIL_1,
    import.meta.env.VITE_ADMIN_EMAIL_2,
    import.meta.env.VITE_ADMIN_EMAIL_3,
  ].filter(Boolean) as string[];

  if (adminEmails.length === 0 && isProduction) {
    console.warn('⚠️ Nenhum email administrativo configurado');
  }

  return {
    env,
    isDevelopment: env === Environment.DEVELOPMENT,
    isStaging: env === Environment.STAGING,
    isProduction,
    isTest: env === Environment.TEST,
    
    // APIs
    apiUrl: import.meta.env.VITE_API_URL || `${window.location.origin}/api`,
    supabaseUrl: import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    supabaseAnonKey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
    supabaseServiceRoleKey: import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY,
    
    // Duffel
    duffelApiUrl: import.meta.env.VITE_DUFFEL_API_URL || DEFAULT_VALUES.duffelApiUrl,
    duffelApiToken,
    duffelApiTokenTest: import.meta.env.VITE_DUFFEL_API_TOKEN_TEST,
    duffelEnvironment,
    
    // Aplicação
    appName: import.meta.env.VITE_APP_NAME || DEFAULT_VALUES.appName,
    appUrl: import.meta.env.VITE_APP_URL || window.location.origin,
    appVersion: import.meta.env.VITE_APP_VERSION || DEFAULT_VALUES.appVersion,
    appDescription: import.meta.env.VITE_APP_DESCRIPTION || DEFAULT_VALUES.appDescription,
    appKeywords: import.meta.env.VITE_APP_KEYWORDS || DEFAULT_VALUES.appKeywords,
    
    // Logging & Analytics
    enableLogging: env !== Environment.PRODUCTION || import.meta.env.VITE_ENABLE_LOGGING === 'true',
    enableAnalytics: isProduction && import.meta.env.VITE_ENABLE_ANALYTICS !== 'false',
    enableDebugTools: env !== Environment.PRODUCTION && import.meta.env.VITE_ENABLE_DEBUG_TOOLS !== 'false',
    
    // SMTP
    smtpHost: import.meta.env.VITE_SMTP_HOST || '',
    smtpPort: parseInt(import.meta.env.VITE_SMTP_PORT) || DEFAULT_VALUES.smtpPort,
    smtpUser: import.meta.env.VITE_SMTP_USER || '',
    smtpPassword: import.meta.env.VITE_SMTP_PASS || '',
    smtpFromEmail: import.meta.env.VITE_FROM_EMAIL || '',
    smtpFromName: import.meta.env.VITE_FROM_NAME || DEFAULT_VALUES.appName,
    
    // Administração
    adminEmails,
    
    // Configurações de moeda
    exchangeRateUsdAoa: parseFloat(import.meta.env.VITE_EXCHANGE_RATE_USD_AOA) || DEFAULT_VALUES.exchangeRateUsdAoa,
    exchangeRateEurAoa: parseFloat(import.meta.env.VITE_EXCHANGE_RATE_EUR_AOA) || DEFAULT_VALUES.exchangeRateEurAoa,
    defaultCurrency: import.meta.env.VITE_DEFAULT_CURRENCY || DEFAULT_VALUES.defaultCurrency,
    
    // Segurança
    sessionTimeout: parseInt(import.meta.env.VITE_SESSION_TIMEOUT) || DEFAULT_VALUES.sessionTimeout,
    maxLoginAttempts: parseInt(import.meta.env.VITE_MAX_LOGIN_ATTEMPTS) || DEFAULT_VALUES.maxLoginAttempts,
  };
};

// ==================== VERIFICAR AMBIENTE ====================
export const isProduction = (): boolean => {
  return getCurrentEnvironment() === Environment.PRODUCTION;
};

export const isDevelopment = (): boolean => {
  return getCurrentEnvironment() === Environment.DEVELOPMENT;
};

export const isStaging = (): boolean => {
  return getCurrentEnvironment() === Environment.STAGING;
};

export const isTest = (): boolean => {
  return getCurrentEnvironment() === Environment.TEST;
};

// ==================== LOG CONDICIONAL ====================
export const log = (...args: any[]): void => {
  if (!isProduction() || import.meta.env.VITE_ENABLE_LOGGING === 'true') {
    console.log('[DG-Travel África]', ...args);
  }
};

export const logInfo = (...args: any[]): void => {
  if (!isProduction() || import.meta.env.VITE_ENABLE_LOGGING === 'true') {
    console.info('[INFO]', ...args);
  }
};

export const logError = (...args: any[]): void => {
  console.error('[ERRO]', ...args);
  
  // Em produção, poderia enviar para serviço de tracking
  if (isProduction()) {
    // TODO: Enviar para serviço de erro como Sentry
  }
};

export const logWarning = (...args: any[]): void => {
  if (!isProduction() || import.meta.env.VITE_ENABLE_LOGGING === 'true') {
    console.warn('[AVISO]', ...args);
  }
};

export const logDebug = (...args: any[]): void => {
  if (isDevelopment() || isTest()) {
    console.debug('[DEBUG]', ...args);
  }
};

// ==================== FUNÇÕES UTILITÁRIAS ====================

/**
 * Verifica se a aplicação está pronta para produção
 */
export const isReadyForProduction = (): {
  ready: boolean;
  issues: string[];
} => {
  const issues: string[] = [];
  const config = getEnvironmentConfig();

  // Verificar variáveis obrigatórias
  const validation = validateEnvironmentVariables();
  if (!validation.valid) {
    issues.push(`Variáveis ausentes: ${validation.missing.join(', ')}`);
  }

  // Verificar configurações críticas
  if (!config.smtpHost) issues.push('SMTP Host não configurado');
  if (!config.smtpUser) issues.push('SMTP User não configurado');
  if (!config.smtpPassword) issues.push('SMTP Password não configurado');
  if (!config.smtpFromEmail) issues.push('Email de envio não configurado');
  
  if (config.adminEmails.length === 0) {
    issues.push('Nenhum email administrativo configurado');
  }

  if (config.duffelEnvironment === 'test') {
    issues.push('Duffel em modo de teste - usar live em produção');
  }

  if (config.duffelApiToken.includes('test')) {
    issues.push('Token Duffel de teste detectado');
  }

  return {
    ready: issues.length === 0,
    issues,
  };
};

/**
 * Obtém URL base da API
 */
export const getApiUrl = (): string => {
  return getEnvironmentConfig().apiUrl;
};

/**
 * Obtém cabeçalhos padrão para requisições
 */
export const getDefaultHeaders = (): Record<string, string> => {
  const config = getEnvironmentConfig();
  
  return {
    'Content-Type': 'application/json',
    'X-App-Version': config.appVersion,
    'X-App-Env': config.env,
  };
};

/**
 * Obtém configuração da Duffel
 */
export const getDuffelConfig = () => {
  const config = getEnvironmentConfig();
  
  return {
    apiUrl: config.duffelApiUrl,
    apiToken: config.duffelApiToken,
    environment: config.duffelEnvironment,
    headers: {
      'Authorization': `Bearer ${config.duffelApiToken}`,
      'Duffel-Version': 'v2',
      'Content-Type': 'application/json',
    },
  };
};

/**
 * Obtém configurações de pagamento
 */
export const getPaymentConfig = () => {
  const config = getEnvironmentConfig();
  
  return {
    multicaixaEntity: import.meta.env.VITE_MULTICAIXA_ENTITY || '11600',
    referenceEntity: import.meta.env.VITE_REFERENCE_ENTITY || '10200',
    bankName: import.meta.env.VITE_BANK_NAME || 'Banco Angolano de Investimentos (BAI)',
    bankNib: import.meta.env.VITE_BANK_NIB || '',
    bankIban: import.meta.env.VITE_BANK_IBAN || '',
    bankSwift: import.meta.env.VITE_BANK_SWIFT || 'BAIAAOLU',
    bankAccountHolder: import.meta.env.VITE_BANK_ACCOUNT_HOLDER || 'DG-Travel África Viagens e Turismo, Lda',
  };
};

// ==================== EXPORTAR CONFIGURAÇÃO ====================
export const config = getEnvironmentConfig();

// Log inicial
logInfo(`🚀 Aplicação iniciada em modo ${config.env}`);
logInfo(`📱 Versão: ${config.appVersion}`);
if (config.isProduction) {
  logInfo('✅ Modo produção ativo');
} else {
  logInfo('🔧 Modo desenvolvimento ativo');
}

export default {
  Environment,
  getCurrentEnvironment,
  validateEnvironmentVariables,
  getEnvironmentConfig,
  isProduction,
  isDevelopment,
  isStaging,
  isTest,
  isReadyForProduction,
  log,
  logInfo,
  logError,
  logWarning,
  logDebug,
  getApiUrl,
  getDefaultHeaders,
  getDuffelConfig,
  getPaymentConfig,
  config,
};